import { useState, useEffect, useCallback } from 'react';
import { contentService, ContentItem } from '../services/contentService.ts';
import { defaultContent } from '../app-config.ts';

// Shared edit-mode content state: loads /api/content into a key->value map and
// saves with the PUT-then-POST upsert pattern (new keys materialize on first save).
export function useContentManager() {
  const [isEditMode, setIsEditMode] = useState(false);
  const [content, setContent] = useState<Record<string, string>>({});
  const [dirtyFields, setDirtyFields] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);

  const loadContent = useCallback(async () => {
    try {
      const contentItems = await contentService.getAllContent();
      const contentMap = contentItems.reduce((acc: Record<string, string>, item: ContentItem) => {
        acc[item.content_key] = item.content_value;
        return acc;
      }, {});
      setContent(contentMap);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Error loading content:', error);
      }
      setContent({});
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadContent();
  }, [loadContent]);

  const handleContentSave = useCallback(
    async (contentKey: string, value: string, contentType: 'text' | 'image' = 'text') => {
      try {
        await contentService.updateContent(contentKey, value, contentType);
        setContent(prev => ({ ...prev, [contentKey]: value }));
      } catch (error) {
        // If update fails (key not in DB yet), create the row instead
        try {
          await contentService.createContent(contentKey, value, contentType);
          setContent(prev => ({ ...prev, [contentKey]: value }));
        } catch (createError) {
          if (process.env.NODE_ENV === 'development') {
            console.error('Error saving content:', createError);
          }
          throw createError;
        }
      }
    },
    []
  );

  const getContent = useCallback(
    (key: string): string => content[key] || defaultContent[key] || '',
    [content]
  );

  const handleDirtyStateChange = useCallback((contentId: string, isDirty: boolean) => {
    setDirtyFields(prev => {
      if (prev.has(contentId) === isDirty) return prev;
      const newSet = new Set(prev);
      if (isDirty) {
        newSet.add(contentId);
      } else {
        newSet.delete(contentId);
      }
      return newSet;
    });
  }, []);

  const handleEditModeToggle = useCallback(() => {
    setIsEditMode(prev => {
      if (prev && dirtyFields.size > 0) {
        const confirmed = window.confirm(
          `您有 ${dirtyFields.size} 個未保存的變更。確定要離開編輯模式嗎？`
        );
        if (!confirmed) return prev;
        setDirtyFields(new Set());
      }
      return !prev;
    });
  }, [dirtyFields]);

  return {
    isEditMode,
    content,
    dirtyFields,
    isLoading,
    loadContent,
    getContent,
    handleContentSave,
    handleDirtyStateChange,
    handleEditModeToggle,
  };
}
